// Angular
import { Inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Local library
import { AccountFacade } from '@amwager/account/data-access';
import { CommonFacade, ENVIRONMENT } from '@amwager/data-access';
import { MatNotificationService } from '@amwager/util';

// Local file
import { FundingFacade } from '../+state';
import { FundingService } from './funding.service';

/** Business logic for the withdraw debit/credit card component. */
@Injectable()
export class WithdrawDebitCreditCardService {

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly fundingFacade: FundingFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Build the withdraw form. */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      amount: ['', [Validators.required, Validators.minLength(25), Validators.maxLength(500)]]
    });
  }

  /** Current TMS id stream. */
  tmsId$() {
    return this.commonFacade?.tmsId$();
  }

  /** Load the saved cards. */
  loadCards$() {
    this.fundingFacade.getDebitCreditCard();
    return this.fundingFacade.getListCards$();
  }

  /** Dispatch the card withdrawal and stream the result. */
  withdraw$(amount: number, orderId: string, tmsId?: string) {
    this.fundingFacade.withdrawDebitCreditCard(amount, orderId, tmsId);
    return this.fundingFacade.isWithdrawDebitCreditCardSuccess$();
  }

  /** Refresh the account balance after a withdrawal. */
  refreshBalance(): void {
    this.accountFacade.getBalance('WagerEffects:withdrawDebitCredit');
  }

  /** Flag the app as offline for the given source. */
  setOffline(source: string): void {
    this.commonFacade.setIsConnectedInternet(source, false);
  }

  /** Mark all controls touched to reveal validation messages. */
  markFormGroupTouched(formGroup: FormGroup): void {
    this.fundingService.markFormGroupTouched(formGroup);
  }

  /** Show a single-button alert. */
  showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
  }

  /** Watch connectivity and report changes back to the caller. */
  checkConnectivity(source: string, onChange: (online: boolean) => void): void {
    this.fundingService.checkConnectivity(this.environment, source, onChange);
  }

}
